import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import orderBy from 'lodash/fp/orderBy';

import { ISkill, JobTalent } from '@bloomays-lib/types.shared';
import { constructDateDetails } from '@bloomays-lib/utils.shared';

import TalentSectionContainer from './TalentSectionContainer';
import CardItem from '../atoms/CardItem';
import SingleLineText, { SingleLineTextProps } from '../atoms/SingleLineText';
import ItemTalentLevel from '../atoms/ItemTalentLevel';

export type TalentExperiencesSectionProps = {
  jobs: JobTalent[];
  addActionFn?: () => void;
  setOpenXP?: (openXP: { item: JobTalent; show: boolean }) => void;
};

const TalentExperiencesSection = ({ jobs, addActionFn, setOpenXP }: TalentExperiencesSectionProps) => {
  const { t } = useTranslation(['talentSheet']);
  return (
    <TalentSectionContainer
      title={t('jobs')}
      emptyText={t('noXp')}
      addActionFn={addActionFn}
      addActionText={t('addXP', { ns: 'talentSheet' })}
    >
      {jobs && jobs.length > 0 && (
        <ContentWrapper>
          {orderBy(
            (item: JobTalent) => item?.period?.startDate,
            ['desc'],
          )(jobs)?.map((job, i) => {
            return (
              <Experience key={job.id}>
                {setOpenXP && (
                  <StyledRowContainerAdd
                    onClick={() => {
                      setOpenXP({
                        item: job,
                        show: true,
                      });
                    }}
                  >
                    <CreateIcon />
                  </StyledRowContainerAdd>
                )}
                <StyledContainerXP>
                  <CardItem
                    variant={'blue'}
                    size={'medium'}
                    emoji={<StyledSpan>{job?.name?.substring(0, 1)}</StyledSpan>}
                  />
                  <StyledContainerXPDetails>
                    <StyledContainerJobTitle>
                      {job?.jobTitle &&
                        job?.jobTitle?.map((j, i) => {
                          return (
                            <div key={`${j.label}-${i}`}>
                              <StyledTitles variant={'heading6'} text={j.label} />
                              {i < (job?.jobTitle?.length as number) - 1 && '/'}
                            </div>
                          );
                        })}
                    </StyledContainerJobTitle>
                    <StyledTitles variant={'subtitle2Medium'} text={job?.name} />
                    <StyledDateText variant={'subtitle1'} text={constructDateDetails(job.period, job?.current)} />
                    <SingleLineText variant={'body1Light'} text={job?.description} />
                    <StyledContainerSkill>
                      {job?.skills?.map((value: ISkill) => (
                        <ItemTalentLevel
                          key={value.id}
                          skill={{
                            label: value.label,
                          }}
                        />
                      ))}
                    </StyledContainerSkill>
                  </StyledContainerXPDetails>
                </StyledContainerXP>
                {jobs && jobs?.length - 1 !== i && <StyledBarXP></StyledBarXP>}
              </Experience>
            );
          })}
        </ContentWrapper>
      )}
    </TalentSectionContainer>
  ) as React.ReactNode;
};

export { TalentExperiencesSection };
export default TalentExperiencesSection;

const ContentWrapper = styled('div')(() => ({
  display: 'flex',
  gap: '16px',
  flexDirection: 'column',
}));

const Experience = styled('div')(
  () => `
  position: relative;
  padding: 12px 0;
  `,
);

const StyledRowContainerAdd = styled('div')(
  ({ theme }) => `
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: flex-end;
    text-decoration: underline;
    position: absolute;
    right: 0;
    color: ${theme.palette.neutral[80]};
    &:hover {
      cursor: pointer;
    }
    `,
);

const StyledContainerXP = styled('div')(
  () => `
    display: flex;
    flex-flow: row nowrap;
    gap: 24px;
    align-items: flex-start;
    `,
);

const StyledTitles: React.FC<SingleLineTextProps> = styled(SingleLineText)(
  ({ theme }) => `
    color: ${theme.palette.neutral[80]};
    `,
);

const StyledContainerXPDetails = styled('div')(
  () => `
    display: flex;
    flex-flow: column nowrap;
    text-align: left;
    max-width: 1280px;
    gap: 8px;
    overflow: hidden;
    `,
);

const StyledContainerJobTitle = styled('div')(
  () => `
    display: flex;
    flex-flow: row wrap;
    gap: 4px;
    align-items: flex-start;
    `,
);

const StyledDateText: React.FC<SingleLineTextProps> = styled(SingleLineText)(
  ({ theme }) => `
    color: ${theme.palette.neutral[30]};
    `,
);

const StyledContainerSkill = styled('div')(
  () => `
    display: flex;
    flex-flow: row wrap; 
    gap: 8px;
    `,
);

const StyledBarXP = styled('div')(
  ({ theme }) => `
    height: 0px;
    margin-top: 32px;
    border: 1px solid ${theme.palette.neutral[20]};
    `,
);

const StyledSpan = styled('span')(
  () => `
  font-size: 16px;
  `,
);
